import React from 'react';
import { Box, Inline, Text } from '@atlaskit/primitives/compiled';
import { expVal } from '@atlassian/jira-feature-experiments';
import { useIntl } from '@atlassian/jira-intl';
import { YourWorkItemListSkeleton } from '@atlassian/jira-skeletons/src/ui/your-work/YourWorkItemListSkeleton.tsx';
import { isMobileAndInViewPanelIterationExperiment } from '@atlassian/jira-mobile-web/src/index.tsx';
import EmptyCannotViewItem from '@atlassian/jira-illustrations/src/ui/adg4/jira/spots/empty/components/empty-cannot-view-item/index.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import {
	VIEWED_TAB_KEY,
	SUCCESS_STATE,
	ERROR_STATE,
	EMPTY_STATE,
} from '../../../common/constants/tab-content.tsx';
import type { ItemProviderDataSection } from '../../../common/types/item-provider.tsx';
import type { TabProps } from '../../../common/types/tabs.tsx';
import EmptyState from '../../../common/ui/empty-state/index.tsx';
import ErrorState from '../../../common/ui/error-state/index.tsx';
import ItemList from '../../../common/ui/item-list/index.tsx';
import ReloadingSpinner from '../../../common/ui/reloading-spinner/index.tsx';
import { LinkWithAnalytics } from '../../../common/ui/with-analytics/index.tsx';
import {
	EndInteractionsTracker,
	trimItemsBySection,
	useFireErrorOrSuccessAnalytics,
} from '../../../common/utils.tsx';
import RecentlyViewedImageUrl from './assets/migration-logo.svg';
import RecentlyViewedTabFooter from './footer/index.tsx';
import messages from './messages.tsx';

export const sumLength = (initialLength: number, section: ItemProviderDataSection) =>
	initialLength + section.items.length;

const RecentlyViewedTab = (props: TabProps) => {
	const emptyStateDescriptionGalaxia = expVal('project-terminology-refresh', 'isEnabled', false)
		? messages.emptyStateDescriptionGalaxia
		: messages.emptyStateDescription;
	const viewAllProjectsGalaxia = expVal('project-terminology-refresh', 'isEnabled', false)
		? messages.viewAllProjectsGalaxia
		: messages.viewAllProjects;
	const {
		itemProviderResult: { data, error, loading, reloading },
		onLoadMoreClick,
		maxItems,
	} = props;

	const { formatMessage } = useIntl();

	useFireErrorOrSuccessAnalytics(!!data.total, error, loading, 'recentlyViewed');

	if (error && !data.total) {
		return (
			<>
				<ErrorState />
				<EndInteractionsTracker transitionKey={VIEWED_TAB_KEY} renderState={ERROR_STATE} />
			</>
		);
	}

	if (loading) {
		return <YourWorkItemListSkeleton />;
	}

	if (
		(!data || !data.sections || data.sections.length === 0) &&
		isMobileAndInViewPanelIterationExperiment()
	) {
		return (
			<>
				<EmptyState
					header={formatMessage(messages.emptyStateHeader)}
					description={formatMessage(messages.emptyStateDescriptionForMobile)}
					renderImage={() => (
						// renderImage doesn't add an image wrapper whereas imageUrl does
						<Box paddingBlockEnd="space.100">
							<Inline alignInline="center">
								<EmptyCannotViewItem alt="" width={154} />
							</Inline>
						</Box>
					)}
				/>
				<EndInteractionsTracker transitionKey={VIEWED_TAB_KEY} renderState={EMPTY_STATE} />
			</>
		);
	}

	if (!data || !data.sections || data.sections.length === 0) {
		return (
			<>
				<EmptyState
					header={formatMessage(messages.emptyStateHeader)}
					description={formatMessage(emptyStateDescriptionGalaxia)}
					imageUrl={RecentlyViewedImageUrl}
					primaryAction={
						<LinkWithAnalytics
							actionSubjectId="recentlyViewedBrowseProjectsButton"
							appearance="primary"
							href="/jira/projects"
						>
							{fg('fix_color_of_home_buttons') ? (
								<Text as="span" color="color.text.inverse">
									{formatMessage(viewAllProjectsGalaxia)}
								</Text>
							) : (
								formatMessage(viewAllProjectsGalaxia)
							)}
						</LinkWithAnalytics>
					}
				/>
				<EndInteractionsTracker transitionKey={VIEWED_TAB_KEY} renderState={EMPTY_STATE} />
			</>
		);
	}

	return (
		<>
			{reloading && <ReloadingSpinner />}
			<ItemList sections={trimItemsBySection(maxItems, data.sections)} />
			<RecentlyViewedTabFooter
				maxItems={maxItems}
				onLoadMoreClick={onLoadMoreClick}
				totalItemCount={data.sections.reduce(sumLength, 0)}
			/>
			<EndInteractionsTracker transitionKey={VIEWED_TAB_KEY} renderState={SUCCESS_STATE} />
		</>
	);
};

export default RecentlyViewedTab;
